<template>
    <section class="landing-page">
        <Hero />
        <Tabs v-if="isLoggedIn" tabsMainClass="tabs-with-background" :wrap="!isMobileView">
            <template v-slot:custom-content>
                <DigitalClassroomsUpdateBanner v-if="shouldShowLiveNotificationBanner"/>
                <CloudProLearningCoachBanner v-if="shouldShowLearningCoachBanner('fi-cloudpro')"/>
                <CloudSkillsLearningCoachBanner v-if="shouldShowLearningCoachBanner('fi-cloudskills') && !hasUserCloudProPackage"/>
                <TaxationDayBanner v-if="shouldShowTaxationDayBanner"/>
            </template>

            <template slot-scope="tabsProps" >
            <Tab @tabMounted="tabsProps.onTabMounted"
                 :activeTab="tabsProps.activeTab"
                 :id="recommendedTabId"
                 :title="$t('LandingPage.tabs_recommended_title')">
                <Recommended />
                <TotemButton
                    class="landing-page__browse-all-button"
                    size="medium"
                    color="primary"
                    @click="goToAllSubjectAreas"
                    :label="$t('LandingPage.browse_all')"
                >
                </TotemButton>
            </Tab>
            <Tab @tabMounted="tabsProps.onTabMounted"
                 :activeTab="tabsProps.activeTab"
                 :title="$t('LandingPage.tabs_my_learning_title')">
                <MyLearning />
            </Tab>
            <Tab @tabMounted="tabsProps.onTabMounted"
                 :activeTab="tabsProps.activeTab"
                 :title="$t('LandingPage.tabs_in_progress_title')">
                <UncompletedCourses />
            </Tab>
            <Tab @tabMounted="tabsProps.onTabMounted"
                 :activeTab="tabsProps.activeTab"
                 :title="$t('LandingPage.tabs_completed_title')">
                <CompletedCourses />
            </Tab>
            </template>
        </Tabs>
        <div v-else class="page-content page-content--no-padding-top">
            <Recommended />
        </div>
        <ModalAddToNewWatchlist/>
    </section>
</template>

<script>
    import { mapGetters } from 'vuex'
    import CompletedCourses from 'views/user/CompletedCourses'
    import UncompletedCourses from 'views/user/UncompletedCourses'
    import Recommended from 'components/landingpage/tabs/Recommended'
    import MyLearning from 'components/landingpage/tabs/MyLearning'
    import Tab from 'components/layout/Tab'
    import Tabs from 'components/layout/Tabs'
    import { viewTypes } from 'store/modules/search'
    import ModalAddToNewWatchlist from 'components/shared/modals/ModalAddToNewWatchlist'
    import Hero from 'components/landingpage/hero/Hero'
    import CloudProLearningCoachBanner from 'components/landingpage/CloudProLearningCoachBanner'
    import CloudSkillsLearningCoachBanner from 'components/landingpage/CloudSkillsLearningCoachBanner'
    import TaxationDayBanner from 'components/landingpage/TaxationDayBanner'
    import DigitalClassroomsUpdateBanner from 'components/landingpage/DigitalClassRoomsUpdateBanner'
    import dateHelper from 'components/shared/mixins/dateHelper'
    import moment from 'moment'

    export default {
        name: 'landing-page',
        components: {
            DigitalClassroomsUpdateBanner,
            CloudSkillsLearningCoachBanner,
            CloudProLearningCoachBanner,
            TaxationDayBanner,
            MyLearning,
            UncompletedCourses,
            CompletedCourses,
            Recommended,
            Tab,
            Tabs,
            ModalAddToNewWatchlist,
            Hero
        },
        mixins: [ dateHelper ],
        computed: {
            ...mapGetters({
                isLoggedIn: 'isLoggedIn',
                userInfo: 'User/getUserInfo',
                viewTypes: 'Search/viewTypes',
                userAccessablePackages: 'User/getUserAccessablePackages',
                language: 'Languages/getSelectedCountryCode',
            }),
            showWatchlistModal () {
                return this.isLoggedIn && this.userInfo?.subordinatesCount && this.sharedWatchlistModalShown === false
            },
            shouldShowLiveNotificationBanner () {
                const relatedPackageCodes = ['fi-business', 'fi-total', 'fi-public']
                const doesUserHaveRelatedPackage = this.userAccessablePackages.some(userPackage => relatedPackageCodes.includes(userPackage.code))
                const now = moment().unix()
                const showLiveEventsNotifyBanner = window.localStorage.getItem('showLiveEventsNotifyBanner') || true
                return showLiveEventsNotifyBanner === true &&
                    now < config.februaryDigitalClassroomsUpdateBannerEndDate &&
                    doesUserHaveRelatedPackage && config.appLanguage === 'fi' && this.language === 'fi'
            },
            shouldShowTaxationDayBanner () {
                switch (config.appLanguage) {
                    case 'se':
                        const showTaxationDayBanner = window.localStorage.getItem('showTaxationDayBanner2023') || true
                        const now = this.getUnixTimeStampByTimeZone()
                        const shouldBannerBeVisibleInSweden = now > config.taxationDaySwedenStart &&
                            now < config.taxationDaySwedenEnd
                        return (this.language === 'se' || this.language === 'en') && showTaxationDayBanner === true && shouldBannerBeVisibleInSweden
                }
                return false
            },
            hasUserCloudProPackage () {
                return this.userAccessablePackages.some(userPackage => userPackage.code === 'fi-cloudpro')
            }
        },
        methods: {
            goToAllSubjectAreas () {
                this.$store.dispatch('Search/setMainPackage', null)
                this.$router.push({ 'name': 'all-packages-all-content' })
            },
            shouldShowLearningCoachBanner (packageCode) {
                const doesUserHaveRelatedPackage = this.userAccessablePackages.some(userPackage => userPackage.code === packageCode)
                return doesUserHaveRelatedPackage && config.appLanguage === 'fi' && this.language === 'fi'
            }
        },
        data () {
            return {
                recommendedTabId: 'tab-recommended'
            }
        },
        provide () {
            return {
                recommendedTabId: this.recommendedTabId
            }
        },
        async created () {
            this.$store.dispatch('Search/disableSearch')
            this.$store.dispatch('Search/setViewType', viewTypes.all)
            this.$store.dispatch('Search/setQuery', null)
            this.$store.dispatch('Search/clearAllFilters', null)
            this.$store.dispatch('Search/setMainPackage', null)
        }
    }
</script>

<style lang="scss">
    @include tabs_with_background;

    .landing-page {
        .tabs-with-background {
            [class*="desktop"] & {
                top: -24px;
            }
        }

        .landing-page__browse-all-button {
            margin: 0 auto;
            display: flex;
            justify-content: center;
            align-items: center;

            [class*="mobile"] & {
                margin: 0 0 20px 0;
                display: flex;
                justify-self: center;
            }
        }
    }
</style>
